import React, { useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Formik, FormikHelpers } from 'formik';
import clsx from 'classnames';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Grid, Typography } from '@material-ui/core';

import { RegistrationForm as RegistrationFormType } from 'types';
import { Jumbotron } from 'components/jumbotron/jumbotron';
import { NotificationCtx } from 'components/notification/notification-hook';
import { NotificationVariant } from 'components/notification/notification-types';
import { useWhoIs } from 'components/auth/auth-hook';
import { AuthCtx } from 'components/auth/auth-ctx';

import { postRegistration } from './registration-api';
import { RegistrationFormBasic } from './registration-form-basic';
import { validationSchema } from './registration-validation';
import { getLoginUrl } from 'utils';

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      marginTop: 50,
    },
    content: {
      padding: '0 0 50px 0',
    },
    center: {
      display: 'flex',
      justifyContent: 'center',
    },
    loginButtonWrapper: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export function RegistrationForm() {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { userInfo, userLoaded } = useContext(AuthCtx);
  const { showNotification } = useContext(NotificationCtx);
  const { whoIsInfo, whoIsLoaded } = useWhoIs();
  const history = useHistory();

  const whoIsInfoExist = !!whoIsInfo;

  const handleSubmit = async (
    values: RegistrationFormType,
    actions: FormikHelpers<RegistrationFormType>
  ) => {
    try {
      const response = await postRegistration(values);

      if (response) {
        showNotification('Registrace odeslána.', NotificationVariant.SUCCESS);
        actions.resetForm();
        history.push('');
      }
    } catch (e) {
      if ((e as any)?.code === 'EMAIL_ALREADY_EXISTS') {
        showNotification(
          'Registrace s tímto emailem již existuje.',
          NotificationVariant.ERROR
        );
      } else {
        showNotification(
          'Registrační formulář se nezdařilo odeslat.',
          NotificationVariant.ERROR
        );
      }
    }

    actions.setSubmitting(false);
  };

  useEffect(() => {
    if (userLoaded && userInfo) {
      history.push('/portfolio/badatelske-listy');
    }
  }, [history, userInfo, userLoaded]);

  return (
    <Grid container>
      <Grid item xs={12}>
        <Jumbotron title={formatMessage({ id: 'registration.jumbotron' })} />
        <Grid container className={clsx(classes.content, classes.center)}>
          <Grid item xs={10} md={5}>
            {whoIsInfoExist ? (
              <Grid container spacing={2} className={classes.center}>
                <Grid item xs={12}>
                  <Typography
                    variant="body1"
                    align="left"
                    className={classes.section}
                  >
                    Data z CAS byly úspěšně načteny, doplňte chýbějící.
                    <br />
                    <br />
                    Tato žádost je žádostí o nahlížení do archiválií a vytvoření
                    badatelského listu dle § 35 zákona č. 499/2004 Sb. o
                    archivnictví a spisové službě v platném znění. Osobní údaje
                    obsažené v badatelském listu jsou shromažďovány a
                    zpracovávány podle zákona č. 499/2004 Sb. za účelem ochrany
                    archiválií a slouží pro vnitřní potřebu archivu a nejsou
                    poskytovány třetím stranám.
                    <br />
                    <br />
                    *vždy povinné údaje
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid container spacing={2} className={classes.center}>
                  <Grid item xs={12}>
                    <Typography
                      variant="body1"
                      align="left"
                      className={classes.section}
                    >
                      Pokud ještě nemáte vytvořený účet v Centrální autentizační
                      službě UK (CAS UK), budou vyplněná data použita pro
                      založení účtu. Účet Vám bude založen po schválení žádosti
                      správcem badatelny Archivu UK.
                      <br />
                      Badatelé, kteří nemají žádný nebo mají pouze neověřený
                      účet v CAS UK, musí osobně navštívit{' '}
                      <a
                        href="https://udauk.cuni.cz/ARCH-32.html"
                        target="__blank"
                        rel="noopener noreferrer"
                      >
                        <b>badatelnu AUK</b>
                      </a>{' '}
                      nebo jakékoli{' '}
                      <a
                        href="https://cuni.cz/UK-3249.html"
                        target="__blank"
                        rel="noopener noreferrer"
                      >
                        <b>výdejní centrum UK</b>
                      </a>{' '}
                      za účelem ověření totožnosti. K ověření totožnosti je
                      nutné předložit platný průkaz totožnosti.
                      <br />
                      <br />
                      Pokud jste byl v období cca od roku 2000 studentem či
                      zaměstnancem UK, pokračujte volbou{' '}
                      <a href={getLoginUrl()} rel="noopener noreferrer">
                        <b>PŘIHLÁSIT SE DO CAS UK</b>
                      </a>
                      , na přihlašovací stránce si případně můžete{' '}
                      <a
                        href="https://ldapuser.cuni.cz/idportal/reset?lang=cs"
                        target="__blank"
                        rel="noopener noreferrer"
                      >
                        <b>obnovit zapomenuté heslo</b>
                      </a>
                      <br />
                      <br />
                      Tato žádost je žádostí o nahlížení do archiválií a
                      vytvoření badatelského listu dle § 35 zákona č. 499/2004
                      Sb. o archivnictví a spisové službě v platném znění.
                      Osobní údaje obsažené v badatelském listu jsou
                      shromažďovány a zpracovávány podle zákona č. 499/2004 Sb.
                      za účelem ochrany archiválií a slouží pro vnitřní potřebu
                      archivu a nejsou poskytovány třetím stranám.
                      <br />
                      <br />
                      *vždy povinné údaje
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
            {whoIsLoaded && (
              <Formik<RegistrationFormType>
                initialValues={whoIsInfo ?? {}}
                onSubmit={handleSubmit}
                validateOnChange={false}
                validateOnBlur={false}
                validateOnMount={false}
                validationSchema={validationSchema}
              >
                {RegistrationFormBasic}
              </Formik>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
